import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Banknote, Check, Clipboard, ClipboardCheck, RefreshCw,
  ScrollText, Upload, User, X,
} from "lucide-react";
import * as api from "../../api";
import { WithdrawalStatusBadge } from "../shared/StatusBadges";

const STATUS_TABS = ["Todos", "Pendiente", "En Proceso", "Rechazado", "Realizado"];

/* -----------------------------------------------------------------------------
 * WithdrawalsPanel — Admin "RETIROS" tab. Módulo independiente de pedidos:
 * lista solicitudes de retiro de revendedores, permite copiar los datos
 * bancarios y cambiar el estado (con referencia/comprobante opcionales).
 * --------------------------------------------------------------------------- */
export function WithdrawalsPanel({ showToast }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("Todos");
  const [copiedKey, setCopiedKey] = useState("");

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const data = await api.listAdminWithdrawals(status === "Todos" ? undefined : status);
      setRows(data);
    } catch (e) {
      showToast?.("Error", e.message || "No se pudo cargar los retiros.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(filter); }, [filter, load]);

  const counts = useMemo(() => {
    const map = { Pendiente: 0, "En Proceso": 0, Rechazado: 0, Realizado: 0 };
    rows.forEach((w) => { if (map[w.status] != null) map[w.status] += 1; });
    return map;
  }, [rows]);

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 1500);
    } catch {
      showToast?.("Error", "No se pudo copiar.", "error");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-withdrawals-view">
      <div className="bg-ag7-surface border border-[var(--ag7-border-soft)] rounded-2xl p-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xl font-heading font-extrabold tracking-tight text-white mb-1">Retiros de Revendedores</h3>
          <p className="text-xs text-zinc-400">Retiro mínimo Bs 500 · Comisión fija 3.5% — módulo independiente de pedidos.</p>
        </div>
        <button onClick={() => load(filter)} data-testid="admin-withdrawals-refresh-btn"
          className="bg-ag7-surface-2 hover:bg-zinc-700 text-white font-bold px-4 py-2 rounded-xl text-xs flex items-center gap-2">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            data-testid={`admin-withdrawals-filter-${tab.replace(/\s+/g, "-").toLowerCase()}`}
            className={`px-4 py-2 rounded-xl text-xs font-bold border transition-colors ${
              filter === tab ? "bg-ag7-purple/20 text-white border-ag7-purple/60" : "text-[var(--ag7-text-muted)] border-transparent hover:bg-ag7-surface-2 hover:text-white"
            }`}
          >
            {tab}{tab !== "Todos" && counts[tab] > 0 ? ` (${counts[tab]})` : ""}
          </button>
        ))}
      </div>

      {loading && rows.length === 0 && (
        <p className="text-xs text-zinc-500 font-mono" data-testid="admin-withdrawals-loading">Cargando retiros…</p>
      )}
      {!loading && rows.length === 0 && (
        <p className="text-xs text-zinc-500 font-mono" data-testid="admin-withdrawals-empty">No hay solicitudes de retiro en este filtro.</p>
      )}

      <div className="space-y-3" data-testid="admin-withdrawals-list">
        {rows.map((w) => (
          <WithdrawalRow key={w.id} w={w} copy={copy} copiedKey={copiedKey} onChanged={() => load(filter)} showToast={showToast} />
        ))}
      </div>
    </div>
  );
}

function CopyBtn({ testId, value, label, copyKey, copy, copiedKey }) {
  const done = copiedKey === copyKey;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => copy(copyKey, value)}
      title={`Copiar ${label}`}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[10px] font-mono text-zinc-300 transition-colors"
    >
      {done ? <ClipboardCheck className="w-3 h-3 text-emerald-400" /> : <Clipboard className="w-3 h-3" />}
      {label}
    </button>
  );
}

function WithdrawalRow({ w, copy, copiedKey, onChanged, showToast }) {
  const [busy, setBusy] = useState(false);
  const [bankReference, setBankReference] = useState(w.bankReference || "");
  const [proofFile, setProofFile] = useState(null);
  const isTerminal = w.status === "Rechazado" || w.status === "Realizado";

  const allDataText = [
    `Cédula: ${w.cedula}`,
    `Teléfono: ${w.telefono}`,
    `Banco: ${w.banco}`,
  ].join("\n");

  const changeStatus = async (status) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.updateWithdrawalStatus(w.id, { status, bankReference, proof: proofFile });
      showToast?.("Retiro actualizado", `${w.code} → ${status}`, "success");
      setProofFile(null);
      await onChanged();
    } catch (e) {
      showToast?.("Error", e.message || "No se pudo actualizar el retiro.", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`admin-withdrawal-row-${w.id}`} className="p-5 rounded-2xl bg-ag7-surface border border-[var(--ag7-border-soft)] space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
            <Banknote className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <p className="text-xs font-mono text-zinc-500">{w.code}</p>
            <p className="text-sm font-bold text-white flex items-center gap-1.5">
              <User className="w-3.5 h-3.5 text-zinc-500" />
              {w.userUsername || w.userEmail || w.userId}
            </p>
          </div>
        </div>
        <WithdrawalStatusBadge status={w.status} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <Field label="Monto" value={`Bs ${Number(w.amountBs).toFixed(2)}`} testId={`admin-withdrawal-amount-${w.id}`} />
        <Field label="Comisión" value={`Bs ${Number(w.commissionBs).toFixed(2)}`} accent="text-amber-300" testId={`admin-withdrawal-commission-${w.id}`} />
        <Field label="Neto a pagar" value={`Bs ${Number(w.netBs).toFixed(2)}`} accent="text-emerald-300" testId={`admin-withdrawal-net-${w.id}`} />
        <Field label="Fecha" value={new Date(w.createdAt).toLocaleString("es-VE")} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <CopyBtn testId={`admin-withdrawal-copy-cedula-${w.id}`} value={w.cedula} label={`Cédula: ${w.cedula}`} copyKey={`cedula-${w.id}`} copy={copy} copiedKey={copiedKey} />
        <CopyBtn testId={`admin-withdrawal-copy-telefono-${w.id}`} value={w.telefono} label={`Tel: ${w.telefono}`} copyKey={`tel-${w.id}`} copy={copy} copiedKey={copiedKey} />
        <CopyBtn testId={`admin-withdrawal-copy-banco-${w.id}`} value={w.banco} label={`Banco: ${w.banco}`} copyKey={`banco-${w.id}`} copy={copy} copiedKey={copiedKey} />
        <CopyBtn testId={`admin-withdrawal-copy-all-${w.id}`} value={allDataText} label="Copiar todo" copyKey={`all-${w.id}`} copy={copy} copiedKey={copiedKey} />
      </div>

      {w.proofUrl && (
        <a href={w.proofUrl} target="_blank" rel="noreferrer" data-testid={`admin-withdrawal-proof-${w.id}`} className="inline-flex items-center gap-1 text-[10px] font-mono text-cyan-300 underline">
          Ver comprobante subido
        </a>
      )}

      {!isTerminal && (
        <div className="pt-3 border-t border-[var(--ag7-border-soft)] space-y-2">
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="text"
              data-testid={`admin-withdrawal-reference-input-${w.id}`}
              value={bankReference}
              onChange={(e) => setBankReference(e.target.value)}
              placeholder="Referencia bancaria (opcional)"
              className="flex-1 min-w-[180px] bg-zinc-950 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-emerald-500"
            />
            <label data-testid={`admin-withdrawal-proof-input-${w.id}`} className="flex items-center gap-1.5 px-3 py-2 border border-dashed border-zinc-700 rounded-lg cursor-pointer hover:bg-zinc-900/40 text-[11px] text-zinc-300">
              <Upload className="w-3.5 h-3.5 text-cyan-400" />
              {proofFile ? proofFile.name : "Comprobante (opcional)"}
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setProofFile(e.target.files?.[0] || null)} />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            {w.status === "Pendiente" && (
              <button onClick={() => changeStatus("En Proceso")} disabled={busy} data-testid={`admin-withdrawal-mark-processing-${w.id}`}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white">
                Marcar En Proceso
              </button>
            )}
            <button onClick={() => changeStatus("Realizado")} disabled={busy} data-testid={`admin-withdrawal-mark-done-${w.id}`}
              className="px-4 py-2 rounded-xl text-xs font-bold bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5" /> Marcar Realizado
            </button>
            <button onClick={() => changeStatus("Rechazado")} disabled={busy} data-testid={`admin-withdrawal-mark-rejected-${w.id}`}
              className="px-4 py-2 rounded-xl text-xs font-bold bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white flex items-center gap-1.5">
              <X className="w-3.5 h-3.5" /> Rechazar
            </button>
          </div>
        </div>
      )}

      {isTerminal && (
        <div className="pt-3 border-t border-[var(--ag7-border-soft)] text-[10px] font-mono text-zinc-500 space-y-1">
          {w.bankReference && <p className="flex items-center gap-1"><ScrollText className="w-3 h-3" />Referencia: {w.bankReference}</p>}
          <p>Procesado por {w.processedByEmail || w.processedBy} · {w.processedAt ? new Date(w.processedAt).toLocaleString("es-VE") : ""}</p>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, accent = "text-white", testId }) {
  return (
    <div>
      <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`font-bold ${accent}`} data-testid={testId}>{value}</p>
    </div>
  );
}
